import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/lib/http';
import { http } from '@/lib/http';
import type { ApiRetailer, ApiStoreFormat, ApiStoreRecord, GalleryImageRecord } from '../lib/mergeStoreProfile';
import { buildDetailFromStore, buildListingFromStore } from '../lib/mergeStoreProfile';
import type { LiveFloorplanRecord, LiveStoreContext, LiveWalkthroughRecord } from '../lib/mapLiveStoreContext';
import { getDemoProfileBySlug } from '../mock-data/profile-overlays';
import type {
  StorefrontPhotoSource,
  StorefrontPhotoVerificationStatus,
  StoreProfileDetail,
  StoreProfileListing,
} from '../types';

async function fetchRetailers(): Promise<ApiRetailer[]> {
  const res = await apiFetch('/api/retailers');
  if (!res.ok) throw new Error('Failed to fetch retailers');
  return res.json();
}

async function fetchStoreFormats(): Promise<ApiStoreFormat[]> {
  const res = await apiFetch('/api/store-formats');
  if (!res.ok) throw new Error('Failed to fetch store formats');
  return res.json();
}

async function fetchStores(retailerId?: number, page = 1): Promise<{ stores: ApiStoreRecord[]; totalCount: number }> {
  const params = new URLSearchParams({
    paginated: 'true',
    page: String(page),
    pageSize: '200',
  });
  if (retailerId != null) {
    params.set('retailerId', String(retailerId));
  }
  const res = await apiFetch(`/api/stores?${params.toString()}`);
  if (!res.ok) throw new Error('Failed to fetch stores');
  const json = await res.json();
  if (Array.isArray(json)) {
    return { stores: json, totalCount: json.length };
  }
  return {
    stores: json.data ?? [],
    totalCount: json.totalCount ?? json.data?.length ?? 0,
  };
}

async function fetchStoreById(storeId: number): Promise<ApiStoreRecord> {
  const res = await apiFetch(`/api/stores/${storeId}`);
  if (!res.ok) throw new Error('Store not found');
  return res.json();
}

interface StorefrontPhotoDescriptor {
  storeId: number;
  photoUrl: string | null;
  imageUrl: string | null;
  hasPhoto: boolean;
  storefrontPhotoSource: StorefrontPhotoSource;
  storefrontPhotoVerificationStatus: StorefrontPhotoVerificationStatus;
  storefrontPhotoSourceUrl: string | null;
  storefrontPhotoSourcePageUrl: string | null;
  storefrontPhotoSourceTitle: string | null;
  photoFocalX: number;
  photoFocalY: number;
}

async function fetchStorefrontPhoto(storeId: number): Promise<StorefrontPhotoDescriptor | null> {
  const res = await apiFetch(`/api/store-profiles/${storeId}/storefront-photo`);
  if (!res.ok) return null;
  return res.json();
}

async function fetchStoreWalkthroughs(storeId: number): Promise<LiveWalkthroughRecord[]> {
  const res = await apiFetch(`/api/store-profiles/${storeId}/walkthroughs`);
  if (!res.ok) return [];
  const json = await res.json();
  return json.walkthroughs ?? [];
}

function mergeLiveContextWithWalkthroughFallback(
  context: LiveStoreContext | null | undefined,
  walkthroughs: LiveWalkthroughRecord[] | undefined,
  storeId: number,
): LiveStoreContext | undefined {
  if (context) {
    if (context.walkthroughs?.length || !walkthroughs?.length) {
      return context;
    }
    return {
      ...context,
      walkthroughs,
      stats: {
        ...context.stats,
        walkthroughCount: walkthroughs.length,
      },
    };
  }
  if (!walkthroughs?.length) {
    return undefined;
  }
  return {
    storeId,
    mediaRequirements: [],
    formatOperatorAssignments: [],
    packs: [],
    assignments: [],
    instoreLocations: [],
    walkthroughs,
    retailerInstoreLocations: [],
    stats: {
      mediaFormatCount: 0,
      totalMediaCapacity: 0,
      packCount: 0,
      activeAssignmentCount: 0,
      activeCampaignCount: 0,
      campaignsToDateCount: 0,
      campaignCount: 0,
      instoreLocationCount: 0,
      walkthroughCount: walkthroughs.length,
      surveyCount: 0,
      referenceImageCount: 0,
    },
  };
}

async function fetchStoreFloorplanRecord(storeId: number): Promise<LiveFloorplanRecord | null> {
  const res = await apiFetch(`/api/store-profiles/${storeId}/floorplan`);
  if (!res.ok) return null;
  const json = await res.json();
  return json.floorplan ?? null;
}

const EMPTY_CONTEXT_STATS: LiveStoreContext['stats'] = {
  mediaFormatCount: 0,
  totalMediaCapacity: 0,
  packCount: 0,
  activeAssignmentCount: 0,
  activeCampaignCount: 0,
  campaignsToDateCount: 0,
  campaignCount: 0,
  instoreLocationCount: 0,
  walkthroughCount: 0,
  surveyCount: 0,
  referenceImageCount: 0,
};

function floorplanOnlyContext(storeId: number, floorplan: LiveFloorplanRecord | null): LiveStoreContext {
  return {
    storeId,
    mediaRequirements: [],
    packs: [],
    assignments: [],
    instoreLocations: [],
    walkthroughs: [],
    floorplan,
    retailerInstoreLocations: [],
    stats: EMPTY_CONTEXT_STATS,
  };
}

async function fetchStoreLiveContext(storeId: number): Promise<LiveStoreContext | null> {
  const res = await apiFetch(`/api/store-profiles/${storeId}`);
  if (!res.ok) return null;
  return res.json();
}

async function fetchStoreGalleryImages(storeId: number): Promise<GalleryImageRecord[]> {
  const params = new URLSearchParams({
    page: '1',
    limit: '200',
    store: String(storeId),
    type: 'photo',
    taskType: 'POSTING',
  });
  const res = await http(`/api/gallery-proxy?${params.toString()}`);
  if (!res.ok) return [];
  const data = await res.json();
  return (data.images ?? []).map((img: GalleryImageRecord) => ({
    id: img.id,
    assetUrl: img.assetUrl,
    thumbnailUrl: img.thumbnailUrl,
    storeName: img.storeName,
    campaignName: img.campaignName,
    operatorName: img.operatorName,
    createdAt: img.createdAt,
    notes: img.notes,
    assignmentId: img.assignmentId,
    campaignId: img.campaignId,
    instoreLocationId: img.instoreLocationId,
  }));
}

export function useStoreProfilesDirectory(retailerId: number | 'all', page = 1) {
  const retailersQuery = useQuery({
    queryKey: ['/api/retailers', 'store-profiles'],
    queryFn: fetchRetailers,
    staleTime: 60_000,
  });

  const formatsQuery = useQuery({
    queryKey: ['/api/store-formats', 'store-profiles'],
    queryFn: fetchStoreFormats,
    staleTime: 60_000,
  });

  const storesQuery = useQuery({
    queryKey: ['/api/stores', 'store-profiles', retailerId, page],
    queryFn: () => fetchStores(retailerId === 'all' ? undefined : retailerId, page),
    staleTime: 30_000,
  });

  const retailersById = new Map((retailersQuery.data ?? []).map((r) => [r.id, r.name]));
  const formatsById = new Map((formatsQuery.data ?? []).map((f) => [f.id, f.name]));

  const listings: StoreProfileListing[] = (storesQuery.data?.stores ?? []).map((store) => {
    const retailerName = store.retailerName ?? retailersById.get(store.retailerId) ?? 'Unknown retailer';
    const formatName = store.formatName ?? (store.formatId ? formatsById.get(store.formatId) ?? null : null);
    return buildListingFromStore(store, retailerName, formatName);
  });

  return {
    listings,
    retailers: retailersQuery.data ?? [],
    totalCount: storesQuery.data?.totalCount ?? 0,
    page,
    isLoading: retailersQuery.isLoading || formatsQuery.isLoading || storesQuery.isLoading,
    isError: retailersQuery.isError || formatsQuery.isError || storesQuery.isError,
    error: retailersQuery.error ?? formatsQuery.error ?? storesQuery.error,
  };
}

/** One compact readiness row per store — from GET /api/store-profiles/summary. */
export interface StoreProfileSummaryRow {
  storeId: number;
  storeName: string;
  address: string;
  retailerId: number | null;
  retailerName: string | null;
  storeCode: string;
  region: string | null;
  formatId: number | null;
  photoUrl: string | null;
  storefrontPhotoSource: StorefrontPhotoSource;
  storefrontPhotoVerificationStatus: StorefrontPhotoVerificationStatus;
  storefrontPhotoSourcePageUrl: string | null;
  photoFocalX: number | null;
  photoFocalY: number | null;
  hasFloorplanImage: boolean;
  aisleCount: number;
  zoneCount: number;
  twinPublishedAt: string | null;
  walkthroughCount: number;
  unmappedWalkthroughCount: number;
  pendingWalkthroughSuggestionCount: number;
  missingThumbnailCount: number;
  surveyCount: number;
  lastSurveyDate: string | null;
  referenceImageCount: number;
  unmappedReferenceImageCount: number;
  pendingImageSuggestionCount: number;
  mediaFormatCount: number;
  totalMediaCapacity: number;
  activeCampaignCount: number;
}

async function fetchStoreProfilesSummary(): Promise<StoreProfileSummaryRow[]> {
  const res = await apiFetch('/api/store-profiles/summary');
  if (!res.ok) throw new Error('Failed to fetch store profile summary');
  const json = await res.json();
  return json.summaries ?? [];
}

/**
 * Fleet-wide readiness summary for the index — one aggregate query server-side,
 * client-side search/filter/pagination so it stays fast across hundreds of stores.
 */
export function useStoreProfilesSummary() {
  const summaryQuery = useQuery({
    queryKey: ['/api/store-profiles/summary'],
    queryFn: fetchStoreProfilesSummary,
    staleTime: 30_000,
  });

  const retailersQuery = useQuery({
    queryKey: ['/api/retailers', 'store-profiles'],
    queryFn: fetchRetailers,
    staleTime: 60_000,
  });

  return {
    summaries: summaryQuery.data ?? [],
    retailers: retailersQuery.data ?? [],
    isLoading: summaryQuery.isLoading || retailersQuery.isLoading,
    isError: summaryQuery.isError,
    error: summaryQuery.error,
  };
}

export function useStoreProfileDetail(storeIdParam: string) {
  const isNumeric = /^\d+$/.test(storeIdParam);
  const numericId = isNumeric ? parseInt(storeIdParam, 10) : null;

  const demoProfile = !isNumeric ? getDemoProfileBySlug(storeIdParam) : undefined;

  const storeQuery = useQuery({
    // Keep this versioned and live-only: an older persisted record may have
    // photoUrl: null and briefly collapse the storefront hero on first paint.
    queryKey: ['/api/stores', storeIdParam, 'detail', 'live-photo-v2'],
    queryFn: () => fetchStoreById(numericId!),
    enabled: numericId != null,
    meta: { persist: false },
  });

  const storefrontQuery = useQuery({
    queryKey: ['/api/store-profiles', storeIdParam, 'storefront-photo'],
    queryFn: () => fetchStorefrontPhoto(numericId!),
    enabled: numericId != null,
    staleTime: 30_000,
    meta: { persist: false },
  });

  const retailersQuery = useQuery({
    queryKey: ['/api/retailers', 'store-profiles'],
    queryFn: fetchRetailers,
    staleTime: 60_000,
  });

  const formatsQuery = useQuery({
    queryKey: ['/api/store-formats', 'store-profiles'],
    queryFn: fetchStoreFormats,
    staleTime: 60_000,
  });

  const floorplanQuery = useQuery({
    queryKey: ['/api/store-profiles', storeIdParam, 'floorplan'],
    queryFn: () => fetchStoreFloorplanRecord(numericId!),
    enabled: numericId != null,
    staleTime: 30_000,
  });

  const liveContextQuery = useQuery({
    queryKey: ['/api/store-profiles', storeIdParam, 'context'],
    queryFn: () => fetchStoreLiveContext(numericId!),
    enabled: numericId != null,
    staleTime: 30_000,
  });

  const walkthroughsQuery = useQuery({
    queryKey: ['/api/store-profiles', storeIdParam, 'walkthroughs'],
    queryFn: () => fetchStoreWalkthroughs(numericId!),
    enabled: numericId != null
      && liveContextQuery.isFetched
      && (liveContextQuery.data?.walkthroughs?.length ?? 0) === 0,
    staleTime: 30_000,
  });

  const galleryQuery = useQuery({
    queryKey: ['/api/gallery-proxy', 'store-profiles', storeIdParam, 'posting'],
    queryFn: () => fetchStoreGalleryImages(numericId!),
    enabled: numericId != null && (floorplanQuery.isFetched || liveContextQuery.isFetched),
    staleTime: 30_000,
  });

  if (demoProfile && !isNumeric) {
    return {
      profile: demoProfile,
      isLoading: false,
      isError: false,
      error: null,
    };
  }

  const baseStore = storeQuery.data;
  const storefront = storefrontQuery.data;
  const store: ApiStoreRecord | undefined = baseStore
    ? {
        ...baseStore,
        ...(storefront
          ? {
              photoUrl: storefront.imageUrl,
              hasStorePhoto: storefront.hasPhoto,
              photoFocalX: storefront.photoFocalX,
              photoFocalY: storefront.photoFocalY,
              storefrontPhotoSource: storefront.storefrontPhotoSource,
              storefrontPhotoVerificationStatus: storefront.storefrontPhotoVerificationStatus,
              storefrontPhotoSourceUrl: storefront.storefrontPhotoSourceUrl,
              storefrontPhotoSourcePageUrl: storefront.storefrontPhotoSourcePageUrl,
              storefrontPhotoSourceTitle: storefront.storefrontPhotoSourceTitle,
            }
          : {}),
      }
    : undefined;
  const retailerRecord = store
    ? retailersQuery.data?.find((r) => r.id === store.retailerId)
    : undefined;
  const retailerName = store
    ? (store.retailerName ?? retailerRecord?.name ?? 'Unknown retailer')
    : '';
  const retailerLogoUrl = retailerRecord?.logo ?? null;
  const formatName = store?.formatName
    ?? (store?.formatId ? formatsQuery.data?.find((f) => f.id === store.formatId)?.name ?? null : null);

  const contextForMap = liveContextQuery.data
    ?? (floorplanQuery.isFetched
      ? floorplanOnlyContext(numericId ?? 0, floorplanQuery.data ?? null)
      : undefined);

  const mergedLiveContext = numericId != null
    ? mergeLiveContextWithWalkthroughFallback(
        contextForMap,
        walkthroughsQuery.data,
        numericId,
      )
    : undefined;

  const profile: StoreProfileDetail | undefined = store
    ? buildDetailFromStore(
        store,
        retailerName,
        formatName ?? null,
        galleryQuery.data ?? [],
        mergedLiveContext,
        retailerLogoUrl,
      )
    : undefined;

  return {
    profile,
    isLoading: storeQuery.isLoading || (numericId != null && !floorplanQuery.isFetched && !liveContextQuery.isFetched),
    isError: storeQuery.isError,
    error: storeQuery.error,
  };
}
